import { useEffect, useState } from "react";
import { inboxCounts } from "./inboxApi";
import { eventBus } from "../utils/eventBus";

/*
 * The unified Inbox's counts (v2 GET /v1/{pid}/inbox/counts): the side nav's
 * "Inbox" badge (total_new) and the queue rail. Cheap like the Form Inbox
 * badge (forms/staff/useInboxCount.js): asked at most once a minute per
 * browser tab, only while the page is visible and a staff sign-in is stored;
 * the answer is shared at module level (the side nav remounts per page). A
 * 404 / 403 (a v2 without the route) hides the counts for this page load.
 * Inbox edits emit "inbox:changed" for a fresh count.
 */
const POLL_MS = 60 * 1000;
const shared = { at: 0, value: null, disabled: false, inflight: null };

const hasStaffToken = () => {
  try {
    return Boolean(window.localStorage.getItem("auth"));
  } catch (e) {
    return false;
  }
};

function fetchCounts() {
  if (shared.inflight) return shared.inflight;
  shared.inflight = inboxCounts()
    .then((d) => {
      shared.value = d && d.queues ? { queues: d.queues, total_new: Number(d.total_new) || 0 } : null;
      shared.at = Date.now();
      return shared.value;
    })
    .catch((e) => {
      const st = e && e.response && e.response.status;
      if (st === 404 || st === 403) {
        shared.disabled = true;
        shared.value = null;
      }
      shared.at = Date.now();
      return shared.value;
    })
    .finally(() => {
      shared.inflight = null;
    });
  return shared.inflight;
}

// Tests only.
export function resetInboxCountsCache() {
  shared.at = 0;
  shared.value = null;
  shared.disabled = false;
  shared.inflight = null;
}

export default function useInboxCounts(enabled) {
  const [counts, setCounts] = useState(() => (enabled && !shared.disabled ? shared.value : null));

  useEffect(() => {
    if (!enabled || shared.disabled) return undefined;
    let alive = true;
    const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const run = (force) => {
      if (shared.disabled || isHidden() || !hasStaffToken()) return;
      if (!force && shared.at && Date.now() - shared.at < POLL_MS) {
        setCounts(shared.value);
        return;
      }
      fetchCounts().then((v) => {
        if (alive) setCounts(shared.disabled ? null : v);
      });
    };
    run(false);
    const timer = setInterval(() => run(true), POLL_MS);
    const onVisible = () => {
      if (!isHidden()) run(false);
    };
    const onChanged = () => run(true);
    document.addEventListener("visibilitychange", onVisible);
    eventBus.on("inbox:changed", onChanged);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      eventBus.off("inbox:changed", onChanged);
    };
  }, [enabled]);

  return enabled ? counts : null;
}
